import { useState, type FormEvent, type ReactNode } from "react";
import { api } from "../api";
import { Logo } from "../brand/Logo";
import { Wordmark } from "../components/Shell";
import { SIGNUP_TERMS_CONSENT, TERMS_VERSION } from "../legal/consent";
import { LEGAL_LINKS } from "../legal/guard";
import { Link, navigate } from "../router";
import { boot } from "../session";

// v2.1 (D): the frame and the two password forms are shared with the Clerk screens in src/auth/screens.tsx.
// `wrap: false` (v4 LG2) drops the "By continuing, you agree" line on screens with the Terms checkbox itself.
export function AuthFrame({ title, lede, children, foot, wrap = true }: { title: string; lede: string; children: ReactNode; foot: ReactNode; wrap?: boolean }) {
  return (
    <div className="auth">
      <div className="auth-art" aria-hidden>
        <Logo layout="stacked" height={220} state="standby" decorative />
      </div>
      <div className="auth-panel">
        <Wordmark named />
        <h1 className="auth-title">{title}</h1>
        <p className="auth-lede">{lede}</p>
        {children}
        <p className="auth-foot">{foot}</p>
        {wrap && (
        <p className="auth-legal">
          By continuing, you agree to our <Link to="/terms">Terms of Service</Link> and <Link to="/acceptable-use">Acceptable Use Policy</Link> and confirm you've read our <Link to="/privacy">Privacy Policy</Link>. You must be 18 or older.
        </p>
        )}
        <p className="auth-legal auth-policies">
          {LEGAL_LINKS.filter((l) => !["privacy", "terms", "acceptable-use"].includes(l.page)).map((l, i) => (
            <span key={l.page}>
              {i > 0 && " · "}
              <Link to={l.path}>{l.short}</Link>
            </span>
          ))}
        </p>
      </div>
    </div>
  );
}

/** v4 LG2: the sign-up Terms checkbox (unticked until the user ticks it). The policies open in a new tab so nothing
 *  typed is lost. */
export function TermsCheck({ checked, onChange }: { checked: boolean; onChange: (v: boolean) => void }) {
  const c = SIGNUP_TERMS_CONSENT;
  const tab = { target: "_blank", rel: "noopener noreferrer" } as const;
  return (
    <div className="auth-terms">
      <label className="auth-check">
        <input type="checkbox" checked={checked} onChange={(e) => onChange(e.target.checked)} required />
        <span>
          {c.agree.before}{" "}
          <a href={c.agree.terms.href} {...tab}>
            {c.agree.terms.text}
          </a>{" "}
          {c.agree.and}{" "}
          <a href={c.agree.aup.href} {...tab}>
            {c.agree.aup.text}
          </a>
          .
        </span>
      </label>
      <p className="auth-legal">
        {c.privacy.before}{" "}
        <a href={c.privacy.link.href} {...tab}>
          {c.privacy.link.text}
        </a>
        . {c.age}
      </p>
    </div>
  );
}

export function Field({ label, hint, ...input }: { label: string; hint?: string } & React.InputHTMLAttributes<HTMLInputElement>) {
  return (
    <label className="field">
      <span className="field-label">{label}</span>
      <input {...input} />
      {hint && <span className="field-hint">{hint}</span>}
    </label>
  );
}

export function InviteScreen({ code }: { code: string }) {
  return (
    <AuthFrame
      title="You're invited"
      lede="Make videos by talking them through. Your Director asks the questions, writes the script, and makes the video while you watch."
      wrap={false} // the form's own Terms checkbox (v4 LG2)
      foot={
        <>
          Already have an account? <Link to="/login">Sign in</Link>
        </>
      }
    >
      <PasswordInviteForm code={code} />
    </AuthFrame>
  );
}

/** Accept an invite with a name, email and password (a password account). */
export function PasswordInviteForm({ code }: { code: string }) {
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [agreed, setAgreed] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const submit = async (e: FormEvent) => {
    e.preventDefault();
    if (!agreed) return;
    setBusy(true);
    setError(null);
    try {
      await api.acceptInvite({ code, name: name.trim(), email: email.trim(), password, termsVersion: TERMS_VERSION });
      await boot({ afterLogin: true });
      navigate("/", { replace: true });
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setBusy(false);
    }
  };
  return (
    <form className="auth-form" onSubmit={submit}>
      <Field label="Your name" value={name} onChange={(e) => setName(e.target.value)} autoComplete="name" required />
      <Field label="Email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} autoComplete="email" inputMode="email" required />
      <Field label="Password" type="password" value={password} onChange={(e) => setPassword(e.target.value)} autoComplete="new-password" minLength={8} hint="At least 8 characters." required />
      <TermsCheck checked={agreed} onChange={setAgreed} />
      {error && (
        <p className="form-error" role="alert">
          {error}
        </p>
      )}
      <button type="submit" className="btn btn-primary btn-block" disabled={busy || !agreed}>
        {busy ? "Creating your account" : "Create account"}
      </button>
    </form>
  );
}

export function LoginScreen() {
  return (
    <AuthFrame title="Welcome back" lede="Sign in and pick up where you and your Director left off." foot={<>CAO Studio is invite-only for now. Your invite link creates your account.</>}>
      <PasswordLoginForm />
    </AuthFrame>
  );
}

/** Email + password sign-in (password accounts). */
export function PasswordLoginForm() {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const submit = async (e: FormEvent) => {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      await api.login(email.trim(), password);
      // sign-in worked; if the very next call has no session, the cookie was dropped: boot says so (never silent)
      await boot({ afterLogin: true });
      navigate("/", { replace: true });
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setBusy(false);
    }
  };
  return (
    <form className="auth-form" onSubmit={submit}>
      <Field label="Email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} autoComplete="email" inputMode="email" required />
      <Field label="Password" type="password" value={password} onChange={(e) => setPassword(e.target.value)} autoComplete="current-password" required />
      {error && (
        <p className="form-error" role="alert">
          {error}
        </p>
      )}
      <button type="submit" className="btn btn-primary btn-block" disabled={busy}>
        {busy ? "Signing in" : "Sign in"}
      </button>
    </form>
  );
}
